import { useState, useCallback, useEffect } from 'react';
import getCMResponse from '../../../utils/api_manager';
import styles from './MetaAuth.module.css';
import MetaIcon from '../../../assets/icons/meta.svg';
import FacebookPagesIcon from '../../../assets/icons/facebook-pages.svg';
import MetaAdsIcon from '../../../assets/icons/meta-ads.svg';
import InstagramIcon from '../../../assets/icons/instagram.svg';
import useMetaBusinessLogin from './useMetaBusinessLogin';
import useInstagramLogin from './useInstagramLogin';

// Unified "Connect Meta" flow (cq-api docs/api/endpoints/datasource.md,
// "Meta — Unified Asset Grant" section, added 2026-07-05). Replaces the
// three previously-separate tiles/components (FacebookPagesAuth, MetaAdsAuth,
// and the old single-account InstagramAuth) because Meta's own Facebook
// Login (for Business) OAuth dialog was never actually separable into three
// independent grants — one dialog grants Pages, Ad Accounts, and Instagram
// accounts together, and can grant multiple of each in a single pass. There
// is deliberately no per-item picker here: nothing is being selected, only
// confirmed and saved in bulk.
//   1. GET  /datasource/meta/oauth/initiate    (via useMetaBusinessLogin)
//   2. POST /datasource/meta/oauth/callback
//   3. GET  /datasource/meta/assets            (list everything granted)
//   4. POST /datasource/meta/assets/save       (server re-fetches + saves
//      everything itself; no asset list is sent)
//
// The old single-account Instagram flow (/datasource/meta/instagram/*,
// provider 'INSTAGRAM') is intentionally left alone on the backend and is no
// longer surfaced by any tile as of this component.
//
// Instagram-only-seller fallback (added 2026-07-05, later still): some
// sellers only have Instagram, no linked Facebook Page, and Facebook Login
// can never surface their account (Pages -> linked IG account is the only
// path it offers). For that case this component also drives the separate
// Instagram Login flow (useInstagramLogin.js, /oauth/instagram/callback) —
//   1. useInstagramLogin() popup -> Instagram's own OAuth dialog
//   2. POST /datasource/instagram/save-access-tokens (code, redirect_uri) ->
//      writes the token onto this project's own INSTAACCOUNT
//      (granted_via='INSTAGRAM_LOGIN') data_sources row directly (its own
//      OAuth base, distinct from the 'META' unified flow above) — see
//      oauth-credential-product-granularity/trd.md Section 1.4/3.1; there is
//      no longer a data_providers row/id to hand back.
//   3. POST /datasource/instagram/save-datasource ({ project_id }) —
//      server re-derives which row to finish from the project's existing
//      INSTAACCOUNT(INSTAGRAM_LOGIN) row, no id passed from the client
// surfaced from two places: the popup-closed-without-completing notice
// below, and inline in the review dialog whenever the granted assets came
// back with no Instagram account.
const ACCESS_PATH_LABEL = {
    DIRECT: 'Direct access',
    BUSINESS_OWNED: 'Via Business Manager',
    BUSINESS_CLIENT: 'Via Business Manager (client)',
};

const ASSET_GROUPS = [
    { key: 'pages', title: 'Pages', icon: FacebookPagesIcon, nameField: 'name' },
    { key: 'ad_accounts', title: 'Ad Accounts', icon: MetaAdsIcon, nameField: 'name' },
    { key: 'instagram_accounts', title: 'Instagram Accounts', icon: InstagramIcon, nameField: 'username' },
];

const MetaAuth = ({
    project,
    onGrantedScopesUpdate,
    onLoginReady,
    // Grant-access loader centralization (requirement 3): mirrors this
    // component's own pre-existing phase/igPhase state machines (unchanged)
    // rather than adding new loading state -- an in-flight network phase on
    // either the Facebook-Login or the direct-Instagram-Login sub-flow
    // raises the same shared per-tile overlay GrantOnboardingAccess renders
    // for every other connector. `reviewing` is deliberately excluded: the
    // review dialog is a user-input step, not a fetch in flight. Optional --
    // only GrantOnboardingAccess supplies it today.
    onLoadingChange,
}) => {
    // idle -> authorizing (popup + oauth callback) -> loading-assets -> reviewing -> saving
    const [phase, setPhase] = useState('idle');
    const [assets, setAssets] = useState({ pages: [], ad_accounts: [], instagram_accounts: [] });
    const [error, setError] = useState(null);
    const [closedNotice, setClosedNotice] = useState(false);

    // Instagram-only-seller fallback (direct Instagram Login, separate OAuth
    // base from the Meta/Facebook-Login flow above). idle -> authorizing
    // (popup) -> connecting (token exchange + save-datasource) -> connected.
    const [igPhase, setIgPhase] = useState('idle');
    const [igError, setIgError] = useState(null);
    const [igAccount, setIgAccount] = useState(null);

    const loadAssets = useCallback((projectId) => {
        setPhase('loading-assets');
        return getCMResponse('/datasource/meta/assets', 'GET', {
            project_id: projectId,
        }).then(result => {
            setAssets({
                pages: result?.data?.pages || [],
                ad_accounts: result?.data?.ad_accounts || [],
                instagram_accounts: result?.data?.instagram_accounts || [],
            });
            setPhase('reviewing');
        }).catch(err => {
            console.error('Error listing Meta assets:', err);
            setError('Could not load your Meta assets. Please try again.');
            setPhase('idle');
        });
    }, []);

    const login = useMetaBusinessLogin({
        projectId: project?.id,
        onSuccess: ({ code, redirect_uri, state }) => {
            setError(null);
            setClosedNotice(false);
            setPhase('authorizing');

            getCMResponse('/datasource/meta/oauth/callback', 'POST', {
                project_id: project.id,
                code,
                redirect_uri,
                state,
            }).then(() => {
                return loadAssets(project.id);
            }).catch(err => {
                console.error('Error completing Meta OAuth callback:', err);
                setError(
                    err?.response?.status === 403
                        ? 'The connection request expired or was tampered with. Please reconnect.'
                        : 'Authentication failed. Please try again.'
                );
                setPhase('idle');
            });
        },
        onError: (err) => {
            console.error('Meta OAuth error:', err);
            setError(err?.message || 'Authentication failed. Please try again.');
            setPhase('idle');
        },
        onClosed: () => {
            // The popup was closed manually before a postMessage completion
            // ever arrived — not necessarily an error (the user may have
            // just changed their mind), so this is shown as a neutral,
            // informational note rather than an error, paired with the
            // direct-Instagram-login recovery action (renderInstagramFallback).
            setClosedNotice(true);
            setPhase('idle');
        },
    });

    const igLogin = useInstagramLogin({
        onSuccess: ({ code, redirect_uri }) => {
            setIgError(null);
            setIgPhase('authorizing');

            getCMResponse('/datasource/instagram/save-access-tokens', 'POST', {
                project_id: project.id,
                code,
                redirect_uri,
            }).then(() => {
                setIgPhase('connecting');
                return getCMResponse('/datasource/instagram/save-datasource', 'POST', {
                    project_id: project.id,
                });
            }).then((saveResult) => {
                setIgAccount(saveResult?.data?.provider_metadata || null);
                setIgPhase('connected');
                onGrantedScopesUpdate();
            }).catch((err) => {
                console.error('Error connecting Instagram directly:', err);
                setIgError('Failed to connect your Instagram account. Please try again.');
                setIgPhase('idle');
            });
        },
        onError: (err) => {
            console.error('Instagram OAuth error:', err);
            setIgError(err?.message || 'Instagram authentication failed. Please try again.');
            setIgPhase('idle');
        },
    });

    const handleConnectInstagramDirect = useCallback(() => {
        if (!project?.id) {
            setIgError('No project selected — cannot start the Instagram connection.');
            return;
        }
        setIgError(null);
        igLogin();
    }, [igLogin, project]);

    const handleContinue = useCallback(() => {
        setPhase('saving');
        setError(null);
        getCMResponse('/datasource/meta/assets/save', 'POST', {
            project_id: project.id,
        }).then(() => {
            setPhase('idle');
            onGrantedScopesUpdate();
        }).catch(err => {
            console.error('Error saving Meta assets:', err);
            setError('Failed to save your Meta assets. Please try again.');
            setPhase('reviewing');
        });
    }, [project, onGrantedScopesUpdate]);

    const handleCancelReview = useCallback(() => {
        setPhase('idle');
        setError(null);
    }, []);

    // Expose login to parent so it can be called directly from a click handler
    // (user gesture context), avoiding popup blocking.
    useEffect(() => {
        onLoginReady?.(login);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Grant-access loader centralization (requirement 3): the only genuine
    // "nothing rendered anywhere near the tile" gap in this component's
    // existing phase machine is `authorizing`/`loading-assets` -- the span
    // between the OAuth popup closing and the review dialog appearing. Once
    // the review dialog is open (`reviewing`/`saving`), or the Instagram
    // fallback button is mid-click, this component already renders its own
    // inline in-context feedback (the "Saving…" button state, the
    // "Connecting…" button state) inside that dialog, so the centralized
    // per-tile overlay would only cover/duplicate an already-visible affordance
    // there -- deliberately not raised for those phases.
    useEffect(() => {
        onLoadingChange?.(phase === 'authorizing' || phase === 'loading-assets');
    }, [phase, onLoadingChange]);

    const showReviewDialog = phase === 'reviewing' || phase === 'saving';
    const totalAssets = assets.pages.length + assets.ad_accounts.length + assets.instagram_accounts.length;

    // Shared recovery action rendered at both trigger points: (a) the
    // popup-closed-without-completing notice, and (b) inline in the review
    // dialog whenever no Instagram account came back in the grant.
    const renderInstagramFallback = () => (
        <div className={styles.igFallbackBox}>
            {igPhase === 'connected' ? (
                <p className={styles.igConnectedText}>
                    ✓ Instagram connected{igAccount?.username ? ` as @${igAccount.username}` : ''}.
                </p>
            ) : (
                <>
                    <button
                        type="button"
                        className={styles.igFallbackButton}
                        disabled={igPhase !== 'idle'}
                        onClick={handleConnectInstagramDirect}
                    >
                        {igPhase === 'idle' ? 'Connect Instagram directly' : 'Connecting…'}
                    </button>
                    {igError && <p className={styles.error}>{igError}</p>}
                </>
            )}
        </div>
    );

    // In managed mode the parent (the platform tile) owns the trigger — this
    // component renders nothing until there's something worth surfacing:
    // in-flight status, an error, a closed-popup notice, the review dialog,
    // or an in-flight/finished direct-Instagram-login fallback (which can
    // still be running after the review dialog itself has been dismissed).
    if (
        onLoginReady &&
        phase === 'idle' &&
        !error &&
        !closedNotice &&
        igPhase === 'idle' &&
        !igError
    ) return null;

    return (
        <div className={styles.container}>
            {!onLoginReady && phase === 'idle' && (
                <img
                    src={MetaIcon}
                    alt="Connect Meta"
                    onClick={() => { setError(null); setClosedNotice(false); login(); }}
                    className={styles.iconButton}
                />
            )}

            {(phase === 'authorizing' || phase === 'loading-assets') && (
                <p className={styles.statusText}>
                    {phase === 'authorizing'
                        ? 'Completing Meta authorization…'
                        : 'Loading your Meta assets…'}
                </p>
            )}

            {showReviewDialog && (
                <div className={styles.dialogOverlay} onClick={handleCancelReview}>
                    <div className={styles.dialogBox} onClick={e => e.stopPropagation()}>
                        <h3 className={styles.dialogTitle}>Review Meta Access</h3>
                        <p className={styles.dialogSubtitle}>
                            Here's everything granted to Cliqmetrics through this connection.
                        </p>

                        {totalAssets === 0 ? (
                            <p className={styles.emptyState}>
                                No Pages, Ad Accounts, or Instagram accounts were found for this
                                connection. Make sure you granted access during the Meta login step,
                                then try connecting again.
                            </p>
                        ) : (
                            <div className={styles.groupList}>
                                {ASSET_GROUPS.map(group => {
                                    const items = assets[group.key];
                                    if (!items || items.length === 0) return null;
                                    return (
                                        <div key={group.key} className={styles.group}>
                                            <h4 className={styles.groupTitle}>{group.title}</h4>
                                            <ul className={styles.accountList}>
                                                {items.map(item => (
                                                    <li key={item.id} className={styles.accountRow}>
                                                        <span className={styles.accountItem}>
                                                            <img
                                                                src={item.profile_picture_url || group.icon}
                                                                alt=""
                                                                className={styles.accountAvatar}
                                                                onError={e => { e.target.src = group.icon; }}
                                                            />
                                                            <span className={styles.accountInfo}>
                                                                <span className={styles.accountName}>
                                                                    {item[group.nameField] || item.name || item.username}
                                                                </span>
                                                                {group.key === 'instagram_accounts' && item.username && (
                                                                    <span className={styles.accountUsername}>@{item.username}</span>
                                                                )}
                                                                {group.key === 'ad_accounts' && item.account_id && (
                                                                    <span className={styles.accountUsername}>{item.account_id} · {item.currency}</span>
                                                                )}
                                                            </span>
                                                            <span
                                                                className={`${styles.accessBadge} ${
                                                                    item.access_path === 'DIRECT'
                                                                        ? styles.accessBadgeDirect
                                                                        : styles.accessBadgeDelegated
                                                                }`}
                                                            >
                                                                {ACCESS_PATH_LABEL[item.access_path] || item.access_path}
                                                            </span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {assets.instagram_accounts.length === 0 && (
                            <div className={styles.igFallbackNotice}>
                                <p className={styles.igFallbackText}>
                                    No Instagram account came back with this grant. If your Instagram
                                    isn't linked to a Facebook Page, connect it directly instead.
                                </p>
                                {renderInstagramFallback()}
                            </div>
                        )}

                        <div className={styles.dialogFooter}>
                            {totalAssets === 0 ? (
                                <button
                                    type="button"
                                    className={styles.cancelButton}
                                    onClick={handleCancelReview}
                                >
                                    Close
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={styles.continueButton}
                                    disabled={phase === 'saving'}
                                    onClick={handleContinue}
                                >
                                    {phase === 'saving' ? 'Saving…' : 'Continue'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {closedNotice && phase === 'idle' && (
                <div className={styles.noticeText}>
                    <p style={{ margin: 0 }}>
                        Didn't finish connecting? If you have an Instagram account that isn't
                        linked to a Facebook Page, you may need to connect it directly instead.
                    </p>
                    {renderInstagramFallback()}
                </div>
            )}

            {/* Direct-Instagram-login fallback still in flight (or just
                finished/errored) after the review dialog that offered it has
                already been dismissed — e.g. the user started the Instagram
                popup from inside the dialog, then closed it via Continue. */}
            {!closedNotice && !showReviewDialog && phase === 'idle' && (igPhase !== 'idle' || igError) && (
                <div className={styles.noticeText}>
                    {renderInstagramFallback()}
                </div>
            )}

            {error && <p className={styles.error}>{error}</p>}
        </div>
    );
};

export default MetaAuth;
